/**
 * BHPH Accounts List Page
 * Browse, search, and manage all BHPH accounts.
 */

import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatCurrency,
  formatDate,
  listBHPHAccounts,
  type BHPHAccount,
} from '../../services/bhphApi';

type StatusFilter = 'ALL' | 'PENDING' | 'ACTIVE' | 'PAID_OFF' | 'CLOSED' | 'REPO';

const STATUS_LABELS: Record<StatusFilter, string> = {
  ALL: 'All',
  PENDING: 'Pending',
  ACTIVE: 'Active',
  PAID_OFF: 'Paid Off',
  CLOSED: 'Closed',
  REPO: 'Repo',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  ACTIVE: 'bg-green-100 text-green-800',
  PAID_OFF: 'bg-blue-100 text-blue-800',
  CLOSED: 'bg-gray-100 text-gray-700',
  REPO: 'bg-red-100 text-red-800',
};

const BHPHAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const {
    data: accounts = [],
    isLoading,
    isError,
    refetch,
  } = useQuery<BHPHAccount[]>({
    queryKey: ['bhphAccounts', statusFilter],
    queryFn: () =>
      listBHPHAccounts({
        limit: 500,
        account_status: statusFilter === 'ALL' ? undefined : statusFilter,
      }),
    refetchInterval: 60_000,
  });

  // Client-side search across visible accounts
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.buyer_name?.toLowerCase().includes(q) ||
        a.account_id?.toLowerCase().includes(q) ||
        a.deal_number?.toLowerCase().includes(q) ||
        a.vehicle_year_make_model?.toLowerCase().includes(q)
    );
  }, [accounts, search]);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">BHPH Accounts</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {isLoading
              ? 'Loading…'
              : `${filtered.length} account${filtered.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/bhph/dashboard')}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            ← Dashboard
          </button>
          <button
            onClick={() => navigate('/bhph/payment')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            💵 Process Payment
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              placeholder="Search by name, account ID, deal #, or vehicle…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status tabs */}
          <div className="flex gap-1 flex-wrap">
            {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <span className="animate-spin mr-2">⏳</span> Loading accounts…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-red-500">
            <p className="font-medium">Failed to load accounts</p>
            <button
              onClick={() => refetch()}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <p className="text-2xl mb-2">📂</p>
            <p className="font-medium text-gray-600 dark:text-gray-300">No accounts found</p>
            {search && (
              <p className="text-sm mt-1">
                No results for "{search}".{' '}
                <button className="text-blue-600 hover:underline" onClick={() => setSearch('')}>
                  Clear search
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-750">
                <tr>
                  {[
                    'Customer',
                    'Deal #',
                    'Vehicle',
                    'Status',
                    'Balance',
                    'Payment',
                    'Days Past Due',
                    'Last Payment',
                    '',
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    onOpen={() => navigate(`/bhph/account/${account.id}`)}
                    onPayment={() => navigate(`/bhph/payment?account=${account.id}`)}
                    onCustomer={
                      account.customer_id
                        ? () => navigate(`/customers/${account.customer_id}`)
                        : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

interface AccountRowProps {
  account: BHPHAccount;
  onOpen: () => void;
  onPayment: () => void;
  onCustomer?: () => void;
}

const AccountRow: React.FC<AccountRowProps> = ({ account, onOpen, onPayment, onCustomer }) => {
  const dpd = account.days_past_due ?? 0;
  const dpdColor =
    dpd === 0
      ? 'text-green-600'
      : dpd <= 7
        ? 'text-yellow-600'
        : dpd <= 30
          ? 'text-orange-600'
          : 'text-red-600';

  return (
    <tr
      className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
      onClick={onOpen}
    >
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
        {account.buyer_name || '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
        <div>{account.deal_number || '—'}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
        {account.vehicle_year_make_model || '—'}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
            STATUS_COLORS[account.account_status] ?? 'bg-gray-100 text-gray-700'
          }`}
        >
          {account.account_status || '—'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
        {formatCurrency(account.total_balance_amount ?? 0)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
        {formatCurrency(account.payment_amount ?? 0)}
        {account.payment_frequency && (
          <div className="text-xs text-gray-400">{account.payment_frequency}</div>
        )}
      </td>
      <td className={`px-4 py-3 text-sm font-semibold whitespace-nowrap ${dpdColor}`}>
        {dpd === 0 ? 'Current' : `${dpd}d`}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDate(account.last_payment_date)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          {onCustomer && (
            <button
              onClick={onCustomer}
              className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              Customer
            </button>
          )}
          <button
            onClick={onOpen}
            className="px-2 py-1 text-xs text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded"
          >
            View
          </button>
          <button
            onClick={onPayment}
            className="px-2 py-1 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 rounded"
          >
            Pay
          </button>
        </div>
      </td>
    </tr>
  );
};

export default BHPHAccountsPage;
